/**
 * The question step model of a Questionnaire-mode wizard: which of a dataset's columns may be
 * asked about, what a question step made from one looks like, where it sits in the step list,
 * and the `content.questionsEnabled` mirror the Add Question dialog renders from.
 *
 * Three writers mint or read question steps - the Add Question dialog, the "Required step"
 * lock, and the effect that seeds a data source's required columns - and they must not drift
 * apart on any of it, so the whole vocabulary lives here rather than in any one of them.
 */

import { type DatasetColumn, getColumnKey } from '@blaze-shared/schema-domain';

import { type WizardStep } from './wizard.models';

/**
 * The columns a questionnaire may ask a question about: everything the builder can see in the
 * Add Question dialog. Internal and computed columns are excluded because nothing can fill
 * them in, which also bounds what may be seeded - a seeded question the dialog cannot offer
 * would be one the builder cannot untick.
 */
export function getWizardQuestionableColumns(columns: DatasetColumn[]): DatasetColumn[] {
  return columns.filter((column) => !column.isInternal && !column.isComputed);
}

/**
 * The columns a questionnaire must ask about: the ones the dataset itself requires. Acquiring
 * a data source seeds a question for each of them, and those questions cannot be made optional
 * (`wizardRequiredAndVisibilityFields` locks their "Required step" toggle on). Deleting one is
 * still the builder's to do - the seed is authorship help, not an enforced invariant, so
 * nothing re-asserts it and the regeneration pass never learns of it.
 */
export function getWizardRequiredQuestionColumns(columns: DatasetColumn[]): DatasetColumn[] {
  return getWizardQuestionableColumns(columns).filter(({ isRequired }) => !!isRequired);
}

/**
 * The question step for a dataset column. Two writers mint one - the Add Question dialog's
 * tick and the required-column seed - and both go through here, so a column's requiredness
 * cannot come to mean one thing when ticked and another when seeded.
 */
export function createWizardQuestionStep(column: DatasetColumn): WizardStep {
  return {
    id: crypto.randomUUID(),
    type: 'data',
    // a column can have only one question, so the column name is a label that is always
    // unique - it also seeds the question Text element when the core creates it
    label: column.name,
    columnKey: getColumnKey(column),
    // a required column makes its question step required - the same rule
    // updateFormGroupDataSource applies to the main form's step in Form mode
    required: !!column.isRequired,
  };
}

/** Adds question steps to a step list, always before the confirmation step that closes it. */
export function insertWizardQuestionSteps(
  steps: WizardStep[],
  questions: WizardStep[],
): WizardStep[] {
  const confirmationIndex = steps.findIndex((step) => step.type === 'confirmation');
  const insertAt = confirmationIndex >= 0 ? confirmationIndex : steps.length;
  return [...steps.slice(0, insertAt), ...questions, ...steps.slice(insertAt)];
}

/**
 * The `content.questionsEnabled` mirror of a step list: the column keys that have a question.
 * The Add Question dialog renders from the mirror alone and drops any step whose column left
 * it, so every writer of the step list must write this in the same breath.
 */
export function getWizardQuestionColumnKeys(steps: WizardStep[]): string[] {
  return steps.map(({ columnKey }) => columnKey).filter((key): key is string => !!key);
}
